import { LOCALIZED_PATHS } from '../constants/url-constants';

export type SupportedAppLocale = 'en' | 'fi' | 'sv';

/**
 * First URL path segment → locale for routes that omit the /en|fi|sv/ prefix
 * (e.g. /tyoelama/... → fi, /arbetsliv/... → sv).
 *
 * Built from LOCALIZED_PATHS so job/location/footer links stay aligned with routing.
 */
function buildFirstSegmentToLocale(): Record<string, SupportedAppLocale> {
  const out: Record<string, SupportedAppLocale> = {};
  const locales: SupportedAppLocale[] = ['en', 'fi', 'sv'];

  for (const locale of locales) {
    for (const value of Object.values(LOCALIZED_PATHS[locale])) {
      const first = String(value).split('/')[0]?.toLowerCase();
      if (!first) continue;
      const existing = out[first];
      if (existing != null && existing !== locale) {
        throw new Error(
          `[infer-locale-from-localized-path-root] First path segment "${first}" maps to both "${existing}" and "${locale}": fix LOCALIZED_PATHS or special-case.`,
        );
      }
      out[first] = locale;
    }
  }

  // Legacy blog aliases (see server/url-rewrite-config redirects)
  out.blog = 'en';
  out.blogi = 'fi';
  out.blogg = 'sv';

  // Job-search guide trees (distinct per locale — not in LOCALIZED_PATHS)
  out['job-search'] = 'en';
  out.tyonhaku = 'fi';
  out.jobbsok = 'sv';

  return out;
}

const FIRST_SEGMENT_TO_LOCALE: Record<string, SupportedAppLocale> = buildFirstSegmentToLocale();

/**
 * When the pathname does not start with /en|fi|sv/, infer locale from the first segment
 * if it matches a localized route root (e.g. tyoelama, tyopaikat, resources).
 */
export function inferLocaleFromLocalizedPathRoot(firstSegment: string): SupportedAppLocale | null {
  const key = firstSegment.toLowerCase();
  return FIRST_SEGMENT_TO_LOCALE[key] ?? null;
}
